import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material'
import Alert from '@mui/material/Alert'
import {
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as DetectIcon,
} from '@mui/icons-material'
import { authenticatedFetch } from '@services/api/apiClient'
import { apiEndpoints } from '../../consts'

const sx = {
  accordion: {
    borderRadius: '16px !important',
    boxShadow: '0 2px 16px rgba(44,62,80,0.08)',
    mb: 3,
    '&:before': { display: 'none' },
    overflow: 'hidden',
  },
  accordionSummary: {
    backgroundColor: 'var(--color-bg-light, #F8FAFC)',
    borderBottom: '1px solid var(--color-border, #E2E8F0)',
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
    },
  },
  sectionAccordion: {
    boxShadow: 'none',
    border: '1px solid var(--color-border, #E2E8F0)',
    borderRadius: '12px !important',
    mb: 1.5,
    '&:before': { display: 'none' },
  },
  sectionHeader: {
    backgroundColor: 'var(--color-bg-light, #FAFBFC)',
    minHeight: '48px !important',
    '& .MuiAccordionSummary-content': {
      margin: '8px 0 !important',
    },
  },
  tableContainer: {
    borderRadius: '12px',
    overflow: 'hidden',
  },
  tableHeader: {
    backgroundColor: 'var(--color-bg-light, #F8FAFC)',
    '& th': {
      fontWeight: 600,
      color: 'var(--color-text-primary, #2E3C52)',
      borderBottom: '2px solid var(--color-border, #E2E8F0)',
      padding: '8px 16px',
    },
  },
  tableRow: {
    '&:hover': {
      backgroundColor: 'var(--color-bg-light, #F8FAFC)',
    },
  },
  headerInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '0.8125rem',
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 12px',
    },
  },
  keyCell: {
    fontFamily: 'monospace',
    fontWeight: 600,
    color: 'var(--color-text-primary, #2E3C52)',
    whiteSpace: 'nowrap',
    width: 80,
  },
  actionButton: {
    textTransform: 'none',
    borderRadius: '10px',
    fontWeight: 600,
    padding: '8px 16px',
  },
  primaryButton: {
    backgroundColor: 'var(--color-primary, #2E3C52)',
    color: '#fff',
    '&:hover': { backgroundColor: 'var(--color-primary-dark, #1E2C42)' },
    '&:disabled': { backgroundColor: '#ccc' },
  },
  outlinedButton: {
    borderColor: 'var(--color-primary, #2E3C52)',
    color: 'var(--color-primary, #2E3C52)',
  },
  badge: {
    ml: 1,
    fontWeight: 600,
    borderRadius: '8px',
  },
}

const SECTION_LABELS = {
  criteriaColumns: {
    label: 'importQA.config.criteria',
    description: 'importQA.config.criteriaDesc',
  },
  sectionNoteColumns: {
    label: 'importQA.config.sectionNotes',
    description: 'importQA.config.sectionNotesDesc',
  },
  metadataColumns: {
    label: 'importQA.config.metadata',
    description: 'importQA.config.metadataDesc',
  },
  scoreColumns: { label: 'importQA.config.scores', description: 'importQA.config.scoresDesc' },
  resultColumns: { label: 'importQA.config.results', description: 'importQA.config.resultsDesc' },
}

export default function QAImportMappingConfig() {
  const { t } = useTranslation()
  const detectFileRef = useRef(null)

  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [detecting, setDetecting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [newCriteriaKey, setNewCriteriaKey] = useState('')

  useEffect(() => {
    fetchConfig()
  }, [])

  const fetchConfig = async () => {
    try {
      setLoading(true)
      const response = await authenticatedFetch(apiEndpoints.qaImportConfig)
      const data = await response.json()
      if (response.ok) {
        setConfig(data.data)
      } else {
        setError(data.message || 'Failed to load config')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      // Strip empty header values before saving
      const stripEmpty = (obj) => {
        const result = {}
        for (const [k, v] of Object.entries(obj)) {
          if (typeof v === 'string' && v.trim()) result[k] = v.trim()
        }
        return result
      }

      const payload = {
        criteriaColumns: stripEmpty(config.criteriaColumns),
        sectionNoteColumns: stripEmpty(config.sectionNoteColumns),
        metadataColumns: stripEmpty(config.metadataColumns),
        scoreColumns: stripEmpty(config.scoreColumns),
        resultColumns: stripEmpty(config.resultColumns),
      }

      const response = await authenticatedFetch(apiEndpoints.qaImportConfig, {
        method: 'PUT',
        body: JSON.stringify(payload),
      })

      const data = await response.json()
      if (response.ok) {
        setConfig(data.data)
        setHasChanges(false)
        setSuccess(t('importQA.config.saved'))
        setTimeout(() => setSuccess(null), 3000)
      } else {
        setError(data.message || 'Failed to save config')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDetect = async (file) => {
    if (!file) return
    try {
      setDetecting(true)
      setError(null)

      const formData = new FormData()
      formData.append('file', file)

      const response = await authenticatedFetch(apiEndpoints.qaImportConfigDetect, {
        method: 'POST',
        body: formData,
      })

      const data = await response.json()
      if (response.ok) {
        const { suggested, unmatched } = data.data
        setConfig((prev) => ({
          ...prev,
          criteriaColumns: { ...prev.criteriaColumns, ...suggested.criteriaColumns },
          sectionNoteColumns: { ...prev.sectionNoteColumns, ...suggested.sectionNoteColumns },
          metadataColumns: { ...prev.metadataColumns, ...suggested.metadataColumns },
          scoreColumns: { ...prev.scoreColumns, ...suggested.scoreColumns },
          resultColumns: { ...prev.resultColumns, ...suggested.resultColumns },
        }))
        setHasChanges(true)

        if (unmatched.length > 0) {
          setSuccess(t('importQA.config.detectedWithUnmatched', { count: unmatched.length }))
        } else {
          setSuccess(t('importQA.config.detected'))
        }
        setTimeout(() => setSuccess(null), 5000)
      } else {
        setError(data.message || 'Failed to detect headers')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setDetecting(false)
      if (detectFileRef.current) detectFileRef.current.value = ''
    }
  }

  const updateMapping = (section, key, value) => {
    setConfig((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }))
    setHasChanges(true)
  }

  const removeMapping = (section, key) => {
    setConfig((prev) => {
      const updated = { ...prev[section] }
      delete updated[key]
      return { ...prev, [section]: updated }
    })
    setHasChanges(true)
  }

  const addCriteria = () => {
    if (!newCriteriaKey || !/^\d+\.\d+$/.test(newCriteriaKey)) return
    if (config.criteriaColumns[newCriteriaKey]) return

    setConfig((prev) => ({
      ...prev,
      criteriaColumns: { ...prev.criteriaColumns, [newCriteriaKey]: '' },
    }))
    setNewCriteriaKey('')
    setHasChanges(true)
  }

  const renderMappingTable = (section, entries, allowAdd = false, allowDelete = false) => {
    const sorted = Object.entries(entries).sort(([a], [b]) => {
      // Sort criteria keys numerically
      if (section === 'criteriaColumns') {
        const [aMaj, aMin] = a.split('.').map(Number)
        const [bMaj, bMin] = b.split('.').map(Number)
        return aMaj !== bMaj ? aMaj - bMaj : aMin - bMin
      }
      return a.localeCompare(b)
    })

    return (
      <Box>
        <TableContainer component={Paper} sx={sx.tableContainer} elevation={0}>
          <Table size="small">
            <TableHead sx={sx.tableHeader}>
              <TableRow>
                <TableCell style={{ width: 100 }}>{t('importQA.config.key')}</TableCell>
                <TableCell>{t('importQA.config.csvHeader')}</TableCell>
                {allowDelete && <TableCell style={{ width: 50 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map(([key, header]) => (
                <TableRow key={key} sx={sx.tableRow}>
                  <TableCell sx={sx.keyCell}>{key}</TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={header}
                      onChange={(e) => updateMapping(section, key, e.target.value)}
                      placeholder={t('importQA.config.headerPlaceholder')}
                      sx={sx.headerInput}
                    />
                  </TableCell>
                  {allowDelete && (
                    <TableCell>
                      <Tooltip title={t('importQA.config.remove')}>
                        <IconButton size="small" onClick={() => removeMapping(section, key)}>
                          <DeleteIcon fontSize="small" style={{ color: '#EF4444' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {allowAdd && (
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <TextField
              variant="outlined"
              size="small"
              value={newCriteriaKey}
              onChange={(e) => setNewCriteriaKey(e.target.value)}
              placeholder="e.g., 4.6"
              style={{ width: 120 }}
              sx={sx.headerInput}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={addCriteria}
              disabled={
                !newCriteriaKey ||
                !/^\d+\.\d+$/.test(newCriteriaKey) ||
                !!config.criteriaColumns[newCriteriaKey]
              }
              startIcon={<AddIcon />}
              sx={sx.actionButton}
            >
              {t('importQA.config.addCriteria')}
            </Button>
          </Box>
        )}
      </Box>
    )
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress size={32} style={{ color: 'var(--color-primary, #2E3C52)' }} />
      </Box>
    )
  }

  if (!config) {
    return error ? (
      <Alert
        severity="error"
        onClose={() => setError(null)}
        style={{ marginBottom: 16, borderRadius: 12 }}
      >
        {error}
      </Alert>
    ) : null
  }

  return (
    <Accordion sx={sx.accordion} defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sx.accordionSummary}>
        <SettingsIcon style={{ color: 'var(--color-primary, #2E3C52)', marginRight: 12 }} />
        <Box>
          <Typography
            variant="subtitle1"
            style={{ fontWeight: 600, color: 'var(--color-text-primary, #2E3C52)' }}
          >
            {t('importQA.config.title')}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {t('importQA.config.subtitle')}
          </Typography>
        </Box>
        {hasChanges && (
          <Chip
            size="small"
            label={t('importQA.config.unsaved')}
            sx={{ ...sx.badge, backgroundColor: '#F59E0B', color: 'white' }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails style={{ flexDirection: 'column', padding: 24 }}>
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            style={{ marginBottom: 16, borderRadius: 12 }}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" style={{ marginBottom: 16, borderRadius: 12 }}>
            {success}
          </Alert>
        )}

        {/* Action buttons */}
        <Box display="flex" justifyContent="flex-end" gap={1.5} mb={2}>
          <input
            ref={detectFileRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={(e) => handleDetect(e.target.files[0])}
          />
          <Button
            variant="outlined"
            onClick={() => detectFileRef.current?.click()}
            disabled={detecting}
            startIcon={detecting ? <CircularProgress size={16} /> : <DetectIcon />}
            sx={{ ...sx.actionButton, ...sx.outlinedButton }}
          >
            {t('importQA.config.detectFromCsv')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !hasChanges}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ ...sx.actionButton, ...sx.primaryButton }}
          >
            {t('importQA.config.save')}
          </Button>
        </Box>

        {/* Sections */}
        {Object.entries(SECTION_LABELS).map(([section, { label, description }]) => (
          <Accordion
            key={section}
            sx={sx.sectionAccordion}
            defaultExpanded={section === 'criteriaColumns'}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sx.sectionHeader}>
              <Box display="flex" alignItems="center">
                <Typography
                  variant="subtitle2"
                  style={{ fontWeight: 600, color: 'var(--color-text-primary, #2E3C52)' }}
                >
                  {t(label)}
                </Typography>
                <Chip
                  size="small"
                  label={Object.keys(config[section] || {}).length}
                  sx={{
                    ...sx.badge,
                    backgroundColor: 'var(--color-bg-light, #E2E8F0)',
                    color: 'var(--color-text-primary, #2E3C52)',
                  }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails style={{ flexDirection: 'column', padding: 16 }}>
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ marginBottom: 12, display: 'block' }}
              >
                {t(description)}
              </Typography>
              {config[section] &&
                renderMappingTable(
                  section,
                  config[section],
                  section === 'criteriaColumns',
                  section === 'criteriaColumns' || section === 'metadataColumns',
                )}
            </AccordionDetails>
          </Accordion>
        ))}
      </AccordionDetails>
    </Accordion>
  )
}
